import React from "react";
import { Box, Button } from "@mui/material";
import { useSnackbarConsumer } from "shared/providers/Snackbar";
import {
  SelectPicker,
  MultiCascader,
  InputGroup,
  Input,
  Checkbox,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseOutlineIcon from "@rsuite/icons/CloseOutline";
import { useFileFilters } from "../../providers/FileFilters";
import { ICamera } from "modules/cameras/types";
import { formatDate } from "shared/parsers/date";
import { ICompany } from "modules/companies/types";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";

import { IFileFilters } from "modules/files/types";

import "rsuite/dist/rsuite.min.css";
import { differenceInDays, parseISO } from "date-fns";

interface IKeyValue {
  label: string;
  value: number;
}

interface IProps {
  isVideoList: boolean;
  isFileScreen?: boolean;
}

const FileFilters: React.FC<IProps> = ({ isVideoList, isFileScreen }) => {
  const {
    companies,
    cameras,
    /*tags,*/ filters,
    setFilters,
    setCompanyId,
    companyId,
    getAll,
  } = useFileFilters();

  const { openSnackbar } = useSnackbarConsumer();
  const [localCompanies, setLocalCompanies] = React.useState<IKeyValue[]>([]);
  const [localCameras, setLocalCameras] = React.useState<IKeyValue[]>([]);
  const [disabledCompanies, setDisabledCompanies] = React.useState<number[]>(
    []
  );
  const [isChecked, setIsChecked] = React.useState<boolean>(false);

  const schema = yup.object().shape({
    query: yup.string(),
    companyId: yup.number(),
    cameraIds: yup.array(),
    noRecordDate: yup.boolean(),
    startDate: yup.string(),
    startTime: yup.string(),
    endDate: yup.string(),
    endTime: yup.string(),
  });

  const { handleSubmit, setValue, control } = useForm<IFileFilters>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: IFileFilters) => {
    if (window.location.pathname !== "/files") {
      if (!data.startDate) {
        openSnackbar({
          message: "Data inicial é obrigatória.",
          severity: "error",
        });
        return false;
      }

      if (!data.endDate) {
        openSnackbar({
          message: "Data final é obrigatória.",
          severity: "error",
        });
        return false;
      }
    }

    if (!companyId) {
      openSnackbar({
        message: "Selecione uma empresa.",
        severity: "error",
      });
      return false;
    }

    if (data.startDate && data.endDate) {
      const diffDays = differenceInDays(
        parseISO(data.endDate),
        parseISO(data.startDate)
      );

      if (diffDays >= 15) {
        openSnackbar({
          message: "O período entre as duas datas é de no máximo 15 dias.",
          severity: "error",
        });
        return false;
      }
    }

    delete data.corporate_name;
    data.noRecordDate = isChecked;
    data.companyId = companyId;
    setFilters({ ...data });

    getAll({ ...data });
  };

  const timeSelect = (time: string, zone: "start" | "end") => {
    if (zone === "start") {
      if (!time) {
        setValue("startDate", "");
        setValue("startTime", "");
      } else {
        setValue("startTime", "00:00");
        setValue("startDate", time);
      }
    }

    if (zone === "end") {
      if (!time) {
        setValue("endDate", "");
        setValue("endTime", "");
      } else {
        setValue("endTime", "23:59");
        setValue("endDate", time);
      }
    }
  };

  const clear = () => {
    setValue("query", "");
    setValue("companyId", 0);
    setValue("cameraIds", []);
    setIsChecked(false);
    setValue("startDate", "");
    setValue("startTime", "");
    setValue("endDate", "");
    setValue("endTime", "");
  };

  React.useEffect(() => {
    const cm: IKeyValue[] = [];
    const cmDeleted: number[] = [];
    companies.forEach((company: ICompany) => {
      cm.push({ label: company.fantasy_name, value: company.id || 0 });
      if (
        company.fantasy_name &&
        company.deleted_at &&
        company.deleted_at !== "0000-00-00 00:00:00"
      ) {
        cmDeleted.push(company.id || 0);
      }
    });
    setLocalCompanies(cm);
    setDisabledCompanies(cmDeleted);
  }, [companies]);

  React.useEffect(() => {
    const cm: IKeyValue[] = [];
    cameras.forEach((camera: ICamera) =>
      cm.push({ label: camera.name, value: camera.id || 0 })
    );

    setLocalCameras(cm);
  }, [cameras]);

  // React.useEffect(() => {
  //   const cm: IKeyValue[] = [];
  //   tags.forEach((tag: ITag) =>
  //     cm.push({ label: tag.name, value: tag.id || 0 })
  //   );
  //   setLocalTags(cm);
  // }, [tags]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        padding: "20px 0",
        borderTop: "1px solid #CCC",
        borderBottom: "1px solid #CCC",
      }}
    >
      <form
        style={{ display: "flex", width: "100%", flexDirection: "column" }}
        onSubmit={handleSubmit(onSubmit)}
      >
        {isVideoList ? (
          <>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                marginTop: "5px",
                alignItems: "space-between",
                justifyContent: "space-between",
              }}
            >
              <div style={{ width: "45%", marginRight: 10 }}>
                {/* <label>
              <b>Nome do arquivo</b>
            </label> */}
                <Controller
                  control={control}
                  name="query"
                  render={({ field: { ...field } }) => (
                    <InputGroup>
                      <Input
                        placeholder="Nome do arquivo"
                        value={filters.query}
                        style={{ width: "100%" }}
                        {...field}
                      />
                      <InputGroup.Addon>
                        <SearchIcon />
                      </InputGroup.Addon>
                    </InputGroup>
                  )}
                />
              </div>
              <div style={{ width: "25%", marginRight: 10 }}>
                {/* <label>
              <b>Empresa</b>
            </label> */}
                <Controller
                  control={control}
                  name="companyId"
                  render={({ field: { ...field } }) => (
                    <SelectPicker
                      data={localCompanies}
                      placeholder="Filtrar por empresa"
                      style={{ width: "100%" }}
                      {...field}
                      onChange={(companyId: number) => {
                        setCompanyId(companyId);
                        setValue("companyId", companyId);
                      }}
                      disabledItemValues={disabledCompanies}
                    />
                  )}
                />
              </div>
              <div style={{ width: "25%", marginRight: 10 }}>
                {/* <label>
              <b>Câmeras</b>
            </label> */}{" "}
                <Controller
                  control={control}
                  name="cameraIds"
                  render={({ field: { ...field } }) => (
                    <MultiCascader
                      data={localCameras}
                      style={{ width: "100%" }}
                      placeholder="Filtrar por câmera"
                      {...field}
                    />
                  )}
                />
              </div>
              <div
                style={{
                  width: "15%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* <label>
              <b>Gravação</b>
            </label> */}

                <Checkbox
                  checked={isChecked}
                  onChange={(event, checked) => setIsChecked(checked)}
                >
                  Sem data
                </Checkbox>
              </div>
            </Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                marginTop: "25px",
              }}
            >
              <div style={{ width: "15%", marginRight: 10 }}>
                <label>
                  <b>Data Inicial</b>
                </label>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field: { ...field } }) => (
                    <Input
                      placeholder="Data inicial"
                      type="date"
                      value={filters.startDate}
                      style={{ width: "100%" }}
                      {...field}
                      onChange={(time) => timeSelect(time, "start")}
                    />
                  )}
                />
              </div>
              <div style={{ width: "15%", marginRight: 10 }}>
                <label>
                  <b>Hora Inicial</b>
                </label>
                <Controller
                  control={control}
                  name="startTime"
                  render={({ field: { ...field } }) => (
                    <Input
                      placeholder="Hora inicial"
                      type="time"
                      value={filters.startTime}
                      style={{ width: "100%" }}
                      {...field}
                    />
                  )}
                />
              </div>
              <div style={{ width: "15%", marginRight: 10 }}>
                <label>
                  <b>Data Final</b>
                </label>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field: { ...field } }) => (
                    <Input
                      placeholder="Data Final"
                      type="date"
                      value={
                        filters.endDate || formatDate(new Date(), "yyyy-MM-dd")
                      }
                      style={{ width: "100%" }}
                      {...field}
                      onChange={(time) => timeSelect(time, "end")}
                    />
                  )}
                />
              </div>
              <div style={{ width: "15%", marginRight: 10 }}>
                <label>
                  <b>Hora Final</b>
                </label>
                <Controller
                  control={control}
                  name="endTime"
                  render={({ field: { ...field } }) => (
                    <Input
                      placeholder="Hora Final"
                      type="time"
                      value={filters.endTime}
                      style={{ width: "100%" }}
                      {...field}
                    />
                  )}
                />
              </div>
              <div
                style={{
                  width: "10%",
                  marginRight: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  marginTop: "15px",
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="medium"
                  startIcon={<SearchIcon />}
                >
                  Procurar
                </Button>
              </div>
              <div
                style={{
                  width: "10%",
                  marginRight: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  marginTop: "15px",
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  size="medium"
                  startIcon={<CloseOutlineIcon />}
                  onClick={() => clear()}
                >
                  Limpar
                </Button>
              </div>
            </Box>
          </>
        ) : (
          <>
            <div
              style={{
                width: "100%",
                marginRight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "left",
                marginTop: "15px",
              }}
            >
              <Controller
                control={control}
                name="companyId"
                render={({ field: { ...field } }) => (
                  <SelectPicker
                    data={localCompanies}
                    placeholder="Filtrar por empresa"
                    style={{ width: "100%" }}
                    {...field}
                    onChange={(companyId: number) => {
                      setCompanyId(companyId);
                      setValue("companyId", companyId);
                    }}
                    disabledItemValues={disabledCompanies}
                  />
                )}
              />
            </div>
            <div
              style={{
                width: "100%",
                marginRight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "left",
                marginTop: "15px",
              }}
            >
              <Controller
                control={control}
                name="query"
                render={({ field: { ...field } }) => (
                  <InputGroup>
                    <Input
                      placeholder="Nome do arquivo"
                      value={filters.query}
                      style={{ width: "100%" }}
                      {...field}
                    />
                    <InputGroup.Addon>
                      <SearchIcon />
                    </InputGroup.Addon>
                  </InputGroup>
                )}
              />
              &nbsp;&nbsp;
              <Button
                type="submit"
                variant="contained"
                size="medium"
                startIcon={<SearchIcon />}
              >
                Procurar
              </Button>
            </div>
          </>
        )}

        {/* <div>
          <MultiCascader
            data={localTags}
            style={{ width: 225 }}
            placeholder="Filtrar por etiquetas/tags"
            onChange={(tags: any) => setFilters({ ...filters, tags })}
          />
        </div> */}
      </form>
    </Box>
  );
};

export { FileFilters };
